import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

// Two intervals: monthly | yearly (yearly ≈ 10 months = 2 months free)
const PLANS = [
  {
    key: "free",
    lookup: { monthly: null, yearly: null },
    name: "Free",
    price: { monthly: "€0", yearly: "€0" },
    period: { monthly: "/mo", yearly: "/yr" },
    accent: "neutral",
    desc: { en: "For solo trials — one project at a time.",
            es: "Para probar en solitario — un proyecto a la vez." },
    features: [
      { en: "1 active project", es: "1 proyecto activo" },
      { en: "Timeline & snags", es: "Cronograma e incidencias" },
      { en: "1 user", es: "1 usuario" },
    ],
  },
  {
    key: "bronze",
    lookup: { monthly: "obra_bronze_monthly", yearly: "obra_bronze_yearly" },
    name: "Bronze",
    price: { monthly: "€19", yearly: "€190" },
    period: { monthly: "/mo", yearly: "/yr" },
    monthly_equivalent: { yearly: "€15.83/mo" },
    accent: "bronze",
    desc: { en: "For small crews juggling a few sites.",
            es: "Para equipos pequeños con varias obras." },
    features: [
      { en: "Up to 3 active projects", es: "Hasta 3 proyectos activos" },
      { en: "Timeline gantt & snags with photos", es: "Cronograma gantt e incidencias con fotos" },
      { en: "PDF snag reports", es: "Informes PDF de incidencias" },
      { en: "Critical snag email alerts", es: "Avisos email de incidencias críticas" },
      { en: "Weather widget per site", es: "Widget de clima por obra" },
    ],
  },
  {
    key: "gold",
    lookup: { monthly: "obra_gold_monthly", yearly: "obra_gold_yearly" },
    name: "Gold",
    price: { monthly: "€49", yearly: "€490" },
    period: { monthly: "/mo", yearly: "/yr" },
    monthly_equivalent: { yearly: "€40.83/mo" },
    accent: "gold",
    desc: { en: "For construction firms running multiple sites.",
            es: "Para empresas con varias obras en curso." },
    features: [
      { en: "Unlimited active projects", es: "Proyectos activos ilimitados" },
      { en: "Everything in Bronze", es: "Todo lo de Bronze" },
      { en: "Priority support", es: "Soporte prioritario" },
    ],
  },
];

const RANK = { free: 0, bronze: 1, gold: 2 };

export default function BillingPage() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [plan, setPlan] = useState(user?.plan || "free");
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(1);
  const [trialEligible, setTrialEligible] = useState(false);
  const [trialDays, setTrialDays] = useState(7);
  const [loadingKey, setLoadingKey] = useState(null);
  const [interval, setInterval] = useState("yearly"); // default to yearly to spotlight savings

  const load = async () => {
    try {
      const { data } = await api.get("/billing/plan");
      setPlan(data.plan);
      setCount(data.project_count || 0);
      setLimit(data.project_limit);
      setTrialEligible(!!data.trial_eligible);
      setTrialDays(data.trial_days || 7);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const upgrade = async (lookup) => {
    if (!lookup) return;
    setLoadingKey(lookup);
    try {
      const { data } = await api.post("/billing/checkout", {
        lookup_key: lookup,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setLoadingKey(null); }
  };

  const openPortal = async () => {
    setLoadingKey("portal");
    try {
      const { data } = await api.post("/billing/portal", {
        return_url: `${window.location.origin}/app/billing`,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setLoadingKey(null); }
  };

  const currentRank = RANK[plan] ?? 0;
  const usagePercent = limit == null ? 0 : Math.min(100, Math.round((count / Math.max(1, limit)) * 100));

  return (
    <div className="max-w-6xl mx-auto space-y-10" data-testid="billing-page">
      <div>
        <p className="label-eyebrow flex items-center"><span className="gold-rule" />{t("billing")}</p>
        <h1 className="mt-3 font-[Fraunces] text-4xl md:text-5xl font-medium tracking-tight text-neutral-950">
          {t("choose_plan")}
        </h1>
        <p className="mt-3 text-neutral-600 max-w-2xl">{t("choose_plan_sub")}</p>
      </div>

      {/* Usage summary */}
      <div className="surface-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <p className="label-eyebrow">{t("your_plan")}</p>
          <div className="mt-1 flex items-center gap-2">
            <p className="font-[Fraunces] text-2xl text-neutral-950 capitalize" data-testid="current-plan-label">{plan}</p>
            {plan !== "free" && (
              <Button variant="outline" size="sm" onClick={openPortal} disabled={loadingKey !== null} className="rounded-full" data-testid="manage-subscription-button">
                {t("manage_subscription")} <ExternalLink className="w-3.5 h-3.5 ml-1" />
              </Button>
            )}
          </div>
        </div>
        <div className="md:w-80">
          <div className="flex items-center justify-between text-xs mb-1.5">
            <span className="label-eyebrow">{t("projects_used")}</span>
            <span className="font-mono text-neutral-700" data-testid="usage-count">
              {count} / {limit == null ? "∞" : limit}
            </span>
          </div>
          <Progress value={limit == null ? 100 : usagePercent} className="h-1.5" />
        </div>
      </div>

      {/* Trial banner */}
      {trialEligible && (
        <div className="surface-card p-5 bg-neutral-950 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-3 border-neutral-950" data-testid="trial-banner">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#B8933A] text-black flex items-center justify-center font-[Fraunces] text-xl leading-none">7</div>
            <div>
              <p className="label-eyebrow text-[#D4B570]">{t("free_trial")}</p>
              <p className="mt-0.5 text-sm text-white/90">{t("trial_banner_desc", { days: trialDays })}</p>
            </div>
          </div>
          <span className="text-[10px] text-white/60 tracking-widest uppercase">{t("cancel_anytime")}</span>
        </div>
      )}

      {/* Interval toggle */}
      <div className="flex items-center justify-center">
        <div className="segment-pill" role="group" aria-label="Billing interval" data-testid="interval-toggle">
          <button
            data-testid="interval-monthly"
            data-active={interval === "monthly"}
            onClick={() => setInterval("monthly")}
            className="segment-btn">
            {t("monthly")}
          </button>
          <button
            data-testid="interval-yearly"
            data-active={interval === "yearly"}
            onClick={() => setInterval("yearly")}
            className="segment-btn flex items-center gap-1.5">
            {t("yearly")}
            <span className="text-[9px] font-bold bg-[#B8933A] text-black px-1.5 py-0.5 rounded tracking-widest">
              −2 {t("months_free_short")}
            </span>
          </button>
        </div>
      </div>

      {/* Three-plan comparison */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {PLANS.map((p) => {
          const isCurrent = plan === p.key;
          const isDowngrade = RANK[p.key] < currentRank;
          const isUpgrade = RANK[p.key] > currentRank;
          const lookup = p.lookup[interval];
          const price = p.price[interval];
          const period = p.period[interval];
          const equivMonthly = p.monthly_equivalent?.[interval];
          const accentBorder = p.accent === "gold" ? "ring-1 ring-[#B8933A]" :
                               p.accent === "bronze" ? "ring-1 ring-[#B87333]" : "";
          return (
            <div key={p.key}
              data-testid={`plan-${p.key}-card`}
              className={`surface-card p-8 relative overflow-hidden flex flex-col ${isCurrent ? "ring-2 ring-neutral-950" : accentBorder}`}>
              <div className="flex items-baseline justify-between">
                <h2 className="font-[Fraunces] text-2xl flex items-center gap-2">
                  {p.name}
                </h2>
                {isCurrent && <Badge className="bg-neutral-950 text-white">{t("current_plan")}</Badge>}
              </div>
              <p className="mt-2 text-sm text-neutral-500">{p.desc[lang] || p.desc.en}</p>
              <div className="mt-6">
                <p className="font-[Fraunces] text-5xl text-neutral-950 leading-none">
                  {price}<span className="text-lg text-neutral-500 font-sans ml-1">{period}</span>
                </p>
                {equivMonthly && (
                  <p className="mt-2 text-xs text-neutral-500">
                    {equivMonthly} · <span className="text-[#B8933A] font-medium">{t("save_2_months")}</span>
                  </p>
                )}
                {!equivMonthly && interval === "yearly" && p.key !== "free" && (
                  <p className="mt-2 text-xs text-neutral-500">&nbsp;</p>
                )}
              </div>
              <ul className="mt-8 space-y-3 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-neutral-700">
                    <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                      p.accent === "gold" ? "text-[#B8933A]" :
                      p.accent === "bronze" ? "text-[#B87333]" : "text-neutral-400"
                    }`} />
                    {f[lang] || f.en}
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                {isCurrent ? (
                  <Button disabled className="w-full rounded-full h-11" variant="outline" data-testid={`btn-${p.key}-current`}>
                    {t("current_plan")}
                  </Button>
                ) : isDowngrade ? (
                  <Button onClick={openPortal} variant="outline" disabled={loadingKey !== null}
                    className="w-full rounded-full h-11" data-testid={`btn-${p.key}-downgrade`}>
                    {t("downgrade_via_portal")}
                  </Button>
                ) : lookup ? (
                  <Button onClick={() => upgrade(lookup)} disabled={loadingKey !== null}
                    className="w-full bg-black hover:bg-neutral-800 rounded-full h-11" data-testid={`btn-${p.key}-upgrade`}>
                    {loadingKey === lookup
                      ? "…"
                      : trialEligible && p.key === "bronze"
                        ? t("start_trial", { days: trialDays })
                        : isUpgrade ? t("upgrade_to") + " " + p.name : t("choose") + " " + p.name}
                  </Button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs text-neutral-500">{t("tax_note")}</p>
    </div>
  );
}
